/**
 * Sketchy (hand-drawn) SVG path generation for diagram nodes and groups.
 *
 * Wraps the rough.js generator (DOM-free) to produce plain SVG path data
 * for every NodeShape at the node's measured pixel dimensions. Generating
 * in pixel space — rather than the normalized 100x100 viewBox the crisp
 * shapes use — keeps the wobble amplitude uniform regardless of the node's
 * aspect ratio (a stretched viewBox would distort the sketch strokes).
 *
 * All output is deterministic for a given seed (see seed.ts), so repeated
 * renders produce byte-identical paths.
 */
import rough from "roughjs";
import type { RoughGenerator } from "roughjs/bin/generator";
import type { Drawable, Options } from "roughjs/bin/core";
import type { NodeShape } from "../types";

/** One renderable SVG path produced by the rough generator. */
export interface SketchPath {
  readonly d: string;
  readonly stroke: string;
  readonly strokeWidth: number;
  readonly fill: string;
}

/** Inputs for sketchy node shape generation. All values in CSS pixels. */
export interface SketchShapeParams {
  readonly shape: NodeShape;
  readonly width: number;
  readonly height: number;
  /** Solid fill color; omit for an unfilled (stroke-only) shape. */
  readonly fill?: string;
  readonly stroke: string;
  readonly strokeWidth: number;
  /** Positive integer seed; identical seeds yield identical paths. */
  readonly seed: number;
}

// Matches the spike-verified look (plans/hand-drawn/spike/roughjs-demo.mjs).
const ROUGHNESS = 1.5;

// The generator is stateless across calls when a seed is supplied, so one
// shared instance is safe.
const generator: RoughGenerator = rough.generator();

function baseOptions(params: {
  readonly stroke: string;
  readonly strokeWidth: number;
  readonly seed: number;
  readonly fill?: string;
}): Options {
  return {
    seed: params.seed,
    roughness: ROUGHNESS,
    stroke: params.stroke,
    strokeWidth: params.strokeWidth,
    ...(params.fill !== undefined
      ? { fill: params.fill, fillStyle: "solid" }
      : {}),
  };
}

function toSketchPaths(drawables: readonly Drawable[]): SketchPath[] {
  return drawables.flatMap((drawable) =>
    generator.toPaths(drawable).map((p) => ({
      d: p.d,
      stroke: p.stroke,
      strokeWidth: p.strokeWidth,
      fill: p.fill ?? "none",
    })),
  );
}

/**
 * Build a rounded-rectangle path with arc corners. rough.js has no
 * rounded-rect primitive, so the path string is fed through generator.path.
 * Radius matches the crisp rendering's CSS borderRadius (16px), clamped so
 * small nodes stay valid.
 */
function roundedRectPath(w: number, h: number, radius: number): string {
  const r = Math.max(0, Math.min(radius, w / 2, h / 2));
  return [
    `M ${String(r)} 0`,
    `L ${String(w - r)} 0`,
    `A ${String(r)} ${String(r)} 0 0 1 ${String(w)} ${String(r)}`,
    `L ${String(w)} ${String(h - r)}`,
    `A ${String(r)} ${String(r)} 0 0 1 ${String(w - r)} ${String(h)}`,
    `L ${String(r)} ${String(h)}`,
    `A ${String(r)} ${String(r)} 0 0 1 0 ${String(h - r)}`,
    `L 0 ${String(r)}`,
    `A ${String(r)} ${String(r)} 0 0 1 ${String(r)} 0`,
    "Z",
  ].join(" ");
}

/**
 * Cylinder mirrors shapes.tsx: body fill, two side lines, bottom cap, then
 * the top cap drawn last so it covers the body's top edge. Cap ry is 12% of
 * height (shapes.tsx uses ry=12 in the 0-100 viewBox).
 */
function cylinderDrawables(
  w: number,
  h: number,
  opts: Options,
  fill: string | undefined,
): Drawable[] {
  const ry = 0.12 * h;
  const strokeOnly: Options = { ...opts, fill: undefined };
  const drawables: Drawable[] = [];
  if (fill !== undefined) {
    // Body fill without a sketchy outline (sides are drawn as lines).
    drawables.push(
      generator.rectangle(0, ry, w, h - 2 * ry, {
        ...opts,
        stroke: "none",
      }),
    );
  }
  drawables.push(
    generator.line(0, ry, 0, h - ry, strokeOnly),
    generator.line(w, ry, w, h - ry, strokeOnly),
    generator.ellipse(w / 2, h - ry, w, 2 * ry, opts),
    generator.ellipse(w / 2, ry, w, 2 * ry, opts),
  );
  return drawables;
}

function shapeDrawables(params: SketchShapeParams): Drawable[] {
  const { shape, width: w, height: h, fill } = params;
  const opts = baseOptions(params);
  switch (shape) {
    case "rectangle":
      return [generator.rectangle(0, 0, w, h, opts)];
    case "rounded-rectangle":
      return [generator.path(roundedRectPath(w, h, 16), opts)];
    case "cylinder":
      return cylinderDrawables(w, h, opts, fill);
    case "diamond":
      return [
        generator.polygon(
          [
            [w / 2, 0],
            [w, h / 2],
            [w / 2, h],
            [0, h / 2],
          ],
          opts,
        ),
      ];
    case "circle":
      return [generator.ellipse(w / 2, h / 2, w, h, opts)];
    case "hexagon": {
      // Flat-top hexagon, ~20% indentation on left/right (mirrors shapes.tsx).
      const indent = 0.2 * w;
      return [
        generator.polygon(
          [
            [indent, 0],
            [w - indent, 0],
            [w, h / 2],
            [w - indent, h],
            [indent, h],
            [0, h / 2],
          ],
          opts,
        ),
      ];
    }
  }
}

/**
 * Generate sketchy SVG paths for a node shape at measured pixel dimensions.
 * Paths are ordered for painting: fill passes precede their stroke passes.
 */
export function sketchShapePaths(params: SketchShapeParams): SketchPath[] {
  return toSketchPaths(shapeDrawables(params));
}

/**
 * Generate a sketchy straight line, e.g. the group header divider.
 * Coordinates in pixel space, deterministic per seed.
 */
export function sketchLinePaths(params: {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly stroke: string;
  readonly strokeWidth: number;
  readonly seed: number;
}): SketchPath[] {
  return toSketchPaths([
    generator.line(
      params.x1,
      params.y1,
      params.x2,
      params.y2,
      baseOptions(params),
    ),
  ]);
}
